import { useCallback, useMemo } from "react";



import { useEditMediaSettings } from "Signups/SignupView/SignupSheet/Screens/SignupItems/CustomizeSignupOptions/api/queries.js";
import { useSignups } from "Signups/state";
import { getBackgroundString } from "Signups/utils/getBackgroundString";
import cloneDeep from "lodash/cloneDeep";



import { ensureLeadingHex } from "Utilities/Colors/colorUtils";
import { monochromatic } from "Utilities/Colors/gradientGeneration/monochromatic";
import { isColorSchemeLight } from "Utilities/Colors/isColorSchemeLight";
import useScreenWidth from "Utilities/Hooks/useScreenWidth";
import removeHtmlTags from "Utilities/removeHtmlTags";

import signupsActions from "./actions";

const useSignupItemsCount = () => {
  const [state] = useSignups();
  const signupItems = state?.currentSignupList?.signupItems;
  return signupItems.length;
};

const useReplaceAllSignupListItems = () => {
  const [, dispatch] = useSignups();

  const replaceAllSignupListItems = useCallback(
    (items) => {
      dispatch({
        type: "replaceAllSignupListItems",
        payload: items,
      });
    },
    [dispatch]
  );

  return replaceAllSignupListItems;
};

const useIsPerSignupCostType = () => {
  const [
    {
      currentSignupList: { isPerSignupCostType },
    },
    dispatch,
  ] = useSignups();

  const setPerSignupCostType = useCallback(
    /**
     * @param {boolean} payload
     */
    (payload) => {
      dispatch(signupsActions.setSignupListIsPerSignupCostType(payload));
    },
    [dispatch]
  );
  return [Boolean(isPerSignupCostType), setPerSignupCostType];
};

const useIsSignupEditMode = () => {
  const [{ isSignupEditMode }, dispatch] = useSignups();

  const setIsSignupEditMode = useCallback(
    (bool) => {
      dispatch({
        type: "setIsSignupEditMode",
        payload: bool,
      });
    },
    [dispatch]
  );

  return { isSignupEditMode, setIsSignupEditMode };
};

const useHasSignupLevelCost = () => {
  const [state] = useSignups();
  const hasSignupCost = state?.currentSignupList?.fundIt?.hasSignupCost;
  return useMemo(() => hasSignupCost, [hasSignupCost]);
};

const useIsSuggestedDonation = () => {
  const [state] = useSignups();
  const isSuggestedDonation =
    state?.currentSignupList?.fundIt?.isSuggestedDonation;
  return useMemo(() => isSuggestedDonation, [isSuggestedDonation]);
};

/**
 * Returns the state of showPollFields and the function for setting it.
 * @returns {[boolean, function]}
 */
const useShowPollFields = () => {
  const [state, dispatch] = useSignups();
  const showPollFields = state?.showPollFields;
  const setShowPollFields = useCallback(
    (payload) => {
      dispatch(signupsActions.setShowPollFields(payload));
    },
    [dispatch]
  );

  return [showPollFields, setShowPollFields];
};

const useEntityPlural = () => {
  const [state] = useSignups();
  return state?.currentSignupList?.signupListVerbs?.entityPlural;
};

const useUserSignupItems = () => {
  const [{ userSignupItems }, dispatch] = useSignups();

  const setUserSignupItems = useCallback(
    (newSignupItems) => {
      dispatch(signupsActions.setUserSignupItems(newSignupItems));
    },
    [dispatch]
  );

  return [userSignupItems, setUserSignupItems];
};

const useUserSignupItem = (itemID) => {
  const [userSignupItems, setUserSignupItems] = useUserSignupItems();

  const userSignupItem = useMemo(
    () => userSignupItems?.[itemID] || {},
    [itemID, userSignupItems]
  );

  const setUserSignupItem = useCallback(
    (newValue) => {
      setUserSignupItems({ ...setUserSignupItems, [itemID]: newValue });
    },
    [itemID, setUserSignupItems]
  );

  return [userSignupItem, setUserSignupItem];
};

const useRespondents = (itemID) => {
  const [userSignupItem, setUserSignupItem] = useUserSignupItem(itemID);
  const respondents = userSignupItem?.respondents || [];
  const setRespondents = useCallback(
    (newRespondents = []) => {
      setUserSignupItem({
        count: newRespondents.length,
        respondents: newRespondents,
      });
    },
    [setUserSignupItem]
  );

  return [respondents, setRespondents];
};

const useRespondentsFromAPI = (itemID) => {
  const [respondents] = useRespondents(itemID);

  const apiRespondents = useMemo(() => {
    const clonedRespondents = cloneDeep(respondents);
    return clonedRespondents
      .sort((a, b) => a.isAdditionalName - b.isAdditionalName) // this is used for display and should be sorted
      .filter(({ userSignupItemID }) => Boolean(userSignupItemID)); // remove anything we munged into the data.
  }, [respondents]);

  return apiRespondents;
};

/**
 * returns setter and getting for respondent in state for the given itemID and userSignupItemID
 * @param {number|string} itemID - the ID for the signup item
 * @param {number|string} userSignupItemID - the userSignupItemID or userID if not yet submitted
 * @returns
 */
const useRespondent = (itemID, userSignupItemID) => {
  const [respondents, setRespondents] = useRespondents(itemID);
  const matchTest = useCallback(
    ({ userSignupItemID: ID, userID }) =>
      userSignupItemID === ID || userSignupItemID === userID,
    [userSignupItemID]
  );
  const respondent = useMemo(
    () => respondents.find(matchTest) || {},
    [matchTest, respondents]
  );

  const setRespondent = useCallback(
    (newRespondentData) => {
      const restOfRespondents = respondents.filter((elem) => !matchTest(elem));
      setRespondents([...restOfRespondents, newRespondentData]);
    },
    [matchTest, respondents, setRespondents]
  );
  return [respondent, setRespondent];
};

const useFetchPriceFlag = () => {
  const [{ fetchPriceFlag }, dispatch] = useSignups();

  const setFetchPriceFlag = useCallback(
    (newFlagValue) => {
      dispatch(signupsActions.setFetchPriceFlag(newFlagValue));
    },
    [dispatch]
  );

  return [fetchPriceFlag, setFetchPriceFlag];
};

const usePaymentAmountNeeded = () => {
  const [{ paymentAmountNeeded }, dispatch] = useSignups();

  const setPaymentAmountNeeded = useCallback(
    (paymentAmountNeeded) => {
      dispatch(signupsActions.setPaymentAmountNeeded(paymentAmountNeeded));
    },
    [dispatch]
  );

  return [paymentAmountNeeded, setPaymentAmountNeeded];
};

function useBackgroundColor(props) {
  // from state
  const [state, dispatch] = useSignups();
  const bgColor = state.currentSignupList?.backgroundColor || "#016099";
  const bgColorScheme =
    state.currentSignupList?.backgroundColorScheme ||
    monochromatic({ color: bgColor });

  // ensures colors all have leading hash
  const backgroundColor = ensureLeadingHex(bgColor).toLowerCase();
  const backgroundColorScheme = bgColorScheme.map((color) =>
    ensureLeadingHex(color).toLowerCase()
  );
  const gradientString = getBackgroundString(backgroundColorScheme);
  const colorVariant = isColorSchemeLight(backgroundColorScheme)
    ? "light"
    : "dark";

  const setBackgroundColorInfo = ({
    backgroundColor,
    backgroundColorScheme,
  }) => {
    dispatch({
      type: "Update Background Color Info",
      backgroundColor,
      backgroundColorScheme,
    });
  };

  return {
    backgroundColor,
    backgroundColorScheme,
    gradientString,
    setBackgroundColorInfo,
    colorVariant,
  };
}

function useMediaSettings() {
  const [state, dispatch] = useSignups();
  const mediaSettings = state.currentSignupList?.media;

  const mediaIsImage = mediaSettings?.type === "image";
  const mediaIsVideo = mediaSettings?.type === "video";

  const { mutate: editMediaSettings } = useEditMediaSettings({
    signupID: state.currentSignupList.ID,
    onSuccess: (res) => {
      dispatch({ type: "Set Media Settings", settings: res || {} });
    },
  });

  const setMediaSettings = async (settings) => {
    await editMediaSettings(settings);
  };

  const setMediaType = async (mediaType) => {
    if (mediaType === "none") {
      dispatch({
        type: "Set Media Settings",
        settings: {
          type: mediaType,
          imageUrl: undefined,
          imageID: undefined,
          videoUrl: undefined,
        },
      });
      await setMediaSettings({ type: "none" });
    } else {
      dispatch({ type: "Set Media Type", mediaType });
      // await setMediaSettings({ type: mediaType });
    }
  };

  return {
    mediaSettings,
    setMediaSettings,
    setMediaType,
    mediaIsImage,
    mediaIsVideo,
  };
}

const useHasDescriptionOrMedia = () => {
  const [state, dispatch] = useSignups();

  const {
    currentSignupList: { description, media },
  } = state;

  const hasDescription = removeHtmlTags(description)?.trim()?.length > 0;
  const hasMedia = media?.type === "image" || media?.type === "video";

  const hasDescriptionOrMedia = hasDescription || hasMedia;

  const screenWidth = useScreenWidth();

  const hasTwoColumns =
    (hasMedia || removeHtmlTags(description).length > 150) &&
    screenWidth >= 1000;

  return { hasDescriptionOrMedia, hasTwoColumns };
};

const useSignupListID = () => {
  const [state, dispatch] = useSignups();

  const { currentSignupList } = state;

  const signupListID = currentSignupList.ID;

  return { signupListID };
};

export {
  useSignupItemsCount,
  useReplaceAllSignupListItems,
  useIsPerSignupCostType,
  useIsSignupEditMode,
  useHasSignupLevelCost,
  useIsSuggestedDonation,
  useShowPollFields,
  useEntityPlural,
  useUserSignupItems,
  useRespondents,
  useRespondent,
  useRespondentsFromAPI,
  useUserSignupItem,
  useFetchPriceFlag,
  usePaymentAmountNeeded,
  useBackgroundColor,
  useMediaSettings,
  useHasDescriptionOrMedia,
  useSignupListID,
};